import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { Cta, Eyebrow, Heading, Reveal } from './primitives';

export default function Hero({ variant }) {
  const videoRef = useRef(null);
  const gestureRetryRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const base = `/funnel/video/${variant.heroVideo}`;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    let cancelled = false;

    // No YouTube fallback, on purpose. It used to take over whenever the clip
    // had not produced a frame within 6 s, which on an ordinary phone
    // connection is most first visits (the clips are 13-16 MB), and the embed
    // itself then failed to load: visitors got a grey broken-page box
    // (depthme.com/mindrest/c, 2026-09-22). The clips ship with the site, so
    // a slow load just shows the poster until the video starts.

    // The ads are voice-over led, so sound has to be there from frame one.
    // Browsers only allow unmuted autoplay after the visitor has interacted
    // with the site (Chrome's engagement index, Safari after any gesture), so:
    //   1. try to play with sound straight away;
    //   2. if that is refused, play muted so the visual still autoplays, and
    //      on the first tap/click anywhere restart the clip from 0 with sound;
    //   3. if even muted autoplay is refused (Low Power Mode), the same first
    //      gesture starts it with sound from 0.
    const playWithSound = () => {
      el.muted = false;
      el.currentTime = 0;
      return el.play();
    };

    const detach = () => {
      if (!gestureRetryRef.current) return;
      window.removeEventListener('pointerdown', gestureRetryRef.current, true);
      window.removeEventListener('keydown', gestureRetryRef.current, true);
      gestureRetryRef.current = null;
    };

    const armGesture = () => {
      if (gestureRetryRef.current) return;
      const onGesture = () => {
        detach();
        if (cancelled) return;
        playWithSound()
          .then(() => setMuted(false))
          .catch(() => {
            // Sound still refused: keep whatever muted playback we have.
            el.muted = true;
            el.play()?.catch(() => {});
          });
      };
      gestureRetryRef.current = onGesture;
      // Capture phase so it fires even when the tap lands on a button.
      window.addEventListener('pointerdown', onGesture, true);
      window.addEventListener('keydown', onGesture, true);
    };

    playWithSound()
      .then(() => { if (!cancelled) setMuted(false); })
      .catch(() => {
        if (cancelled) return;
        // Safari only honours muted autoplay when the property is set before play().
        el.muted = true;
        setMuted(true);
        el.play()?.catch(() => {});
        armGesture();
      });

    return () => {
      cancelled = true;
      detach();
    };
  }, [variant.heroVideo]);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.muted) {
      // Turning sound on: replay from the start so the voice-over is heard whole.
      el.muted = false;
      el.currentTime = 0;
      el.play()?.catch(() => {});
      setMuted(false);
    } else {
      el.muted = true;
      setMuted(true);
    }
  };

  return (
    <section className="relative overflow-hidden px-4 pb-16 pt-28 md:pt-36">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* Dan's hero: flat black with one soft gold glow, no violet. */}
        <div className="absolute left-1/2 top-[-10%] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#F5A623]/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto flex max-w-3xl flex-col items-center text-center">
        <Reveal><Eyebrow>{variant.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <Heading as="h1" lines={variant.headline} className="text-[2.4rem] sm:text-5xl md:text-7xl" />
        </Reveal>

        <Reveal delay={0.18} className="mt-10 w-full max-w-[300px]">
          <div className="relative aspect-[9/16] overflow-hidden gold-glow rounded-2xl border border-[#F5A623]/25 bg-[#111111]">
            {(
              <>
                <video
                  key={variant.heroVideo}
                  ref={videoRef}
                  className="absolute inset-0 h-full w-full object-cover"
                  poster={`${base}_poster.webp`}
                  autoPlay
                  loop
                  playsInline
                  preload="auto"
                >
                  <source src={`${base}.webm`} type="video/webm" />
                  <source src={`${base}.mp4`} type="video/mp4" />
                </video>
                <button
                  type="button"
                  onClick={toggleSound}
                  aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
                  className="absolute bottom-3 right-3 flex h-10 items-center gap-2 rounded-full border border-white/15 bg-black/45 px-3 text-xs text-white backdrop-blur-md"
                >
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  {muted ? 'Tap for sound' : 'Sound on'}
                </button>
              </>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.26}>
          <p className="mx-auto mt-10 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
            {variant.sub}
          </p>
        </Reveal>
        <Reveal delay={0.32} className="mt-8 flex flex-col items-center gap-3">
          <Cta variant={variant} />
          <p className="text-xs text-white/50">✓ Start your personalized experience in minutes.</p>
        </Reveal>
      </div>
    </section>
  );
}
